import { findStrategy, findFund, findCust, findBrokerAbb } from "../../component/Methods";

export const SSANDC_ROUTER_ID = "0ea492818ab8eda55cb6bd0760647d77";
export const GET_SSANDC_FILLS_REQUEST = "get_ssandc_fills";
export const ALLOCATE_SSANDC_TRADES_REQUEST = "allocate_ssandc_trades";
export const CANCEL_SSANDC_TRADES_REQUEST = "cancel_ssandc_trades";

export function getStartOfLocalDay(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function getLastOrder(tradeObj) {
  if (!tradeObj?.orders?.length) return null;
  return tradeObj.orders[tradeObj.orders.length - 1];
}

export function abbreviationIncludesSsandc(abbreviation) {
  return (abbreviation || "").toLowerCase().includes("ss&c");
}

export function routerDisplayIncludesSsandc(routerDisplay) {
  return (routerDisplay || "").toLowerCase().includes("ss&c");
}

export function getSsandcRouterId(routes) {
  return (
    routes?.find((r) =>
      abbreviationIncludesSsandc((r?.abbreviation || "").trim())
    )?.routerId ?? SSANDC_ROUTER_ID
  );
}

export function findRouteByRouterId(routes, routerId) {
  if (routerId == null || routerId === "" || !Array.isArray(routes)) return null;
  return (
    routes.find(
      (r) =>
        r?.routerId === routerId || String(r?.routerId) === String(routerId)
    ) || null
  );
}

export function isRouterIdSet(routerId) {
  return (
    routerId != null &&
    routerId !== "" &&
    String(routerId).trim() !== ""
  );
}

/** SS&C routed (router set and matches SS&C); allocation may already exist. */
export function isSsandcRoutedOnlySelectionItem(t, routes) {
  const rid = t?.tradeObject?.router?.router;
  if (!isRouterIdSet(rid)) return false;
  const match = findRouteByRouterId(routes, rid);
  return abbreviationIncludesSsandc(match?.abbreviation);
}

/** SS&C routed with no allocation yet (eligible to allocate). */
export function isSsandcRoutedSelectionItem(t, routes) {
  if (t?.tradeObject?.allocation != null) return false;
  return isSsandcRoutedOnlySelectionItem(t, routes);
}

export function getTradeIdsFromSelection(items) {
  return items.map((trade) => String(trade?.tradeId));
}

export function getEligibleTradeIdsFromSelection(
  selectedTrades,
  routes,
  isEligibleFn
) {
  if (!Array.isArray(selectedTrades) || selectedTrades.length === 0) return [];
  return selectedTrades
    .filter((t) => isEligibleFn(t, routes))
    .map((t) => String(t.tradeId));
}

export function isLocalToday(d) {
  const s = getStartOfLocalDay();
  return (
    d.getFullYear() === s.getFullYear() &&
    d.getMonth() === s.getMonth() &&
    d.getDate() === s.getDate()
  );
}

/** UTC calendar-day match for order timestamps vs selected local date. */
export function isOrderOnSelectedDateUtc(orderDate, selectedDate) {
  if (!orderDate || !selectedDate) return false;
  const selectedDateUTC = Date.UTC(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate()
  );
  const selectedDateStartUTC = new Date(selectedDateUTC);
  const selectedDateEndUTC = new Date(
    selectedDateUTC + 24 * 60 * 60 * 1000 - 1
  );
  const order = new Date(orderDate);
  const orderDateUTC = Date.UTC(
    order.getUTCFullYear(),
    order.getUTCMonth(),
    order.getUTCDate(),
    order.getUTCHours(),
    order.getUTCMinutes(),
    order.getUTCSeconds(),
    order.getUTCMilliseconds()
  );
  return (
    orderDateUTC >= selectedDateStartUTC.getTime() &&
    orderDateUTC <= selectedDateEndUTC.getTime()
  );
}

export function tradeRowMatchesSearch(item, searchTerm) {
  if (!searchTerm) return true;
  const q = searchTerm.toLowerCase();
  return (
    item.ticker.toLowerCase().includes(q) ||
    item.fund.toLowerCase().includes(q) ||
    item.strategy.toLowerCase().includes(q) ||
    item.side.toLowerCase().includes(q)
  );
}

export function filterTradeRowsBySelectedDate(rows, selectedDate) {
  if (!selectedDate) return rows;
  return rows.filter((item) => isOrderOnSelectedDateUtc(item.orderDate, selectedDate));
}

export function filterTradeRowsByListFilters(
  rows,
  { searchTerm, selectedEntity, selectedStrategy, selectedTicker }
) {
  let data = rows;
  if (searchTerm) {
    data = data.filter((item) => tradeRowMatchesSearch(item, searchTerm));
  }
  if (selectedEntity) {
    data = data.filter((item) => item.fund === selectedEntity);
  }
  if (selectedStrategy) {
    data = data.filter((item) => item.strategy === selectedStrategy);
  }
  if (selectedTicker) {
    data = data.filter((item) => item.ticker === selectedTicker);
  }
  return data;
}

export function collectUniqueTradeFieldValues(transformedData, field) {
  const valueSet = new Set();
  transformedData.forEach((trade) => {
    if (trade[field] !== "N/A") {
      valueSet.add(trade[field]);
    }
  });
  return Array.from(valueSet);
}

export function sortTickersLettersBeforeNumbers(tickers) {
  return tickers.sort((a, b) => {
    const aIsNumber = /^\d/.test(a);
    const bIsNumber = /^\d/.test(b);
    if (aIsNumber && !bIsNumber) return 1;
    if (!aIsNumber && bIsNumber) return -1;
    return a.localeCompare(b);
  });
}

export function formatOrderTimestamp(date) {
  if (!date) return "00:00:00";
  const dateObj = new Date(date);
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  const seconds = String(dateObj.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

export function parseDistributionKeyParts(distributionKey) {
  const parts = distributionKey.split("#");
  if (parts.length < 3) return null;
  return {
    account: parts[0],
    fundHash: parts[1],
    entityType: parts[2],
    strategyHash: parts[3],
  };
}

export function resolveDistributionEntities(
  distributionKey,
  { funds, strategies, custodians }
) {
  const parsed = parseDistributionKeyParts(distributionKey);
  if (!parsed) {
    return { fundName: "N/A", strategyName: "N/A", custodianName: "N/A", account: "" };
  }
  const { account, fundHash, strategyHash } = parsed;
  const fundName = findFund(fundHash, funds) || "N/A";
  const strategyName = findStrategy(strategyHash, strategies) || "N/A";
  let custodianName = "N/A";
  const selectedFund = funds.find((fund) => fund.fundId === fundHash);
  if (selectedFund?.accounts?.[account]) {
    const custodianId = selectedFund.accounts[account].custodian;
    custodianName = findCust(custodianId, custodians) || "N/A";
  }
  return { fundName, strategyName, custodianName, account };
}

/** Shared fill % / state logic for manual and auto-fill branches. */
export function resolveFilledPercentAndState(
  totalOrderedShares,
  filledShares,
  isBrokerCross
) {
  let state = "Working";
  if (totalOrderedShares === filledShares) {
    state = "Ready";
  }
  if (isBrokerCross) {
    return { state: "Ready", filledPercent: 100 };
  }
  const filledPercent =
    totalOrderedShares > 0
      ? Math.round((filledShares / totalOrderedShares) * 100)
      : 0;
  return { state, filledPercent };
}

export function resolveWorkingOrderFillMetrics(isBrokerCross) {
  if (isBrokerCross) {
    return { state: "Ready", filledPercent: 100, pricePerShare: 0, filledShares: 0 };
  }
  return { state: "Working", filledPercent: 0, pricePerShare: 0, filledShares: 0 };
}

export function buildTradeTypeDisplay(action, position, isOptionTrade) {
  let tradeType = "";
  let isOpeningPosition = false;

  if (isOptionTrade) {
    if (action === "Buy" && position === "Long") {
      tradeType = "BUY TO OPEN";
      isOpeningPosition = true;
    } else if (action === "Sell" && position === "Long") {
      tradeType = "SELL TO CLOSE";
      isOpeningPosition = false;
    } else if (action === "Sell" && position === "Short") {
      tradeType = "SELL TO OPEN";
      isOpeningPosition = true;
    } else if (action === "Buy" && position === "Short") {
      tradeType = "BUY TO CLOSE";
      isOpeningPosition = false;
    } else {
      tradeType = `${action} ${position}`;
      isOpeningPosition = false;
    }
  } else if (action === "Buy" && position === "Long") {
    tradeType = "BUY LONG";
    isOpeningPosition = true;
  } else if (action === "Sell" && position === "Short") {
    tradeType = "SELL SHORT";
    isOpeningPosition = true;
  } else if (action === "Buy" && position === "Short") {
    tradeType = "COVER SHORT";
    isOpeningPosition = false;
  } else if (action === "Sell" && position === "Long") {
    tradeType = "SELL LONG";
    isOpeningPosition = false;
  } else {
    tradeType = `${action} ${position}`;
    isOpeningPosition = action === "Buy";
  }

  return { tradeType, isOpeningPosition };
}

export function isCommissionEligibleTrade(item, selectedDate, brokers) {
  const lastOrder = getLastOrder(item);
  if (!lastOrder) return false;
  const brokerAbb = findBrokerAbb(lastOrder.broker, brokers);
  if (brokerAbb == null || brokerAbb === "cros") return false;
  const lastOrderDate = new Date(lastOrder.date);
  const lastOrderDateUTC = Date.UTC(
    lastOrderDate.getUTCFullYear(),
    lastOrderDate.getUTCMonth(),
    lastOrderDate.getUTCDate()
  );
  const selectedDateUTC = Date.UTC(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate()
  );
  return lastOrderDateUTC === selectedDateUTC;
}

export function buildCommissionPayloadEntry(trade, brokers) {
  const lastOrder = getLastOrder(trade);
  const brokerAbb = findBrokerAbb(lastOrder.broker, brokers);
  const brokerLower = brokerAbb.toLowerCase();
  let commission = 0;
  if (brokerLower === "gspt") {
    commission = 0.01;
  } else if (brokerLower === "cfr") {
    commission = 0.0065;
  }
  return { trade: trade.tradeId, commission };
}
